import { h, Fragment } from 'preact';
import { useState, useEffect, useMemo, useRef } from 'preact/hooks';
import Panel from 'utils/panel';
import CloseIcon from 'utils/icon/icon-close.svg';
import NavigationIcon from 'utils/icon/icon-navigation.svg';
import { getCookie } from 'utils/helpers';
import StoreItem from './store-item';
import PlacesAutocomplete from './places-autocomplete';
import { useLocations } from './locations-context';
import StoreProduct from './store-product';

const StoreLocator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [linkedLocation, setLinkedLocation] = useState(null);
  const searchInput = useRef(null);
  const storeRefs = useRef({});

  const {
    allLocations,
    userCurrentLocation,
    useMyLocation,
    userSavedStoreId,
    results,
    loadingResults,
    locationServicesError,
    stockData,
    noResults,
    popularLocations,
  } = useLocations();

  const userSavedLocation = useMemo(() => {
    if (!allLocations?.[userSavedStoreId]) {
      return null;
    }
    const savedStore = allLocations[userSavedStoreId];
    if (userCurrentLocation) {
      const metersAway = google.maps.geometry.spherical.computeDistanceBetween(
        {
          lat: userCurrentLocation.lat,
          lng: userCurrentLocation.lng,
        },
        {
          lat: savedStore.address.latitude,
          lng: savedStore.address.longitude,
        },
      );
      return {
        ...savedStore,
        metersAway,
        kmAway: (metersAway / 1000).toFixed(1),
      };
    }
    return savedStore;
  }, [allLocations, userSavedStoreId, userCurrentLocation]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlLocationId = searchParams.get('storeLocation');
    if (urlLocationId) {
      const urlLocation = allLocations[urlLocationId];
      if (urlLocation) {
        if (userCurrentLocation) {
          const metersAway =
            google.maps.geometry.spherical.computeDistanceBetween(
              {
                lat: userCurrentLocation.lat,
                lng: userCurrentLocation.lng,
              },
              {
                lat: urlLocation.address.latitude,
                lng: urlLocation.address.longitude,
              },
            );
          setLinkedLocation({
            ...urlLocation,
            metersAway,
            kmAway: (metersAway / 1000).toFixed(1),
          });
        } else {
          setLinkedLocation(urlLocation);
        }

        openStoreLocator();
        searchParams.delete('storeLocation');
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${
            parseInt(searchParams?.size, 10) > 0 ? '?' : ''
          }${searchParams}`,
        );
      }
    }
  }, [allLocations]);

  const showPopularLocations = results.length === 0;

  const openStoreLocator = (event = {}) => {
    setIsOpen(true);
    if (event.detail?.storeId && event.detail.storeId !== userSavedStoreId) {
      setTimeout(() => {
        if (storeRefs.current[event.detail.storeId]) {
          storeRefs.current[event.detail.storeId].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 100);
    }
    if (
      navigator.geolocation &&
      !getCookie('geo_disabled') &&
      !getCookie('geo_enabled')
    ) {
      useMyLocation();
    }
  };

  // Listen for panel open events
  useEffect(() => {
    const hashChangeHandle = (e) => {
      if (e.newURL.includes('#store-locator')) {
        window.history.replaceState(
          null,
          '',
          window.location.href.split('#')[0],
        );
        openStoreLocator();
      }
    };

    if (window.location.hash.includes('store-locator')) {
      window.history.replaceState(null, '', window.location.href.split('#')[0]);
      openStoreLocator();
    }

    document.addEventListener('openStoreLocator', openStoreLocator);
    window.addEventListener('hashchange', hashChangeHandle);
    return () => {
      document.removeEventListener('openStoreLocator', openStoreLocator);
      window.removeEventListener('hashchange', hashChangeHandle);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setLinkedLocation(null);
  }

  return (
    <Panel
      additionalClasses={`bg-white overflow-hidden`}
      align="right"
      context="store-locator"
      onClose={handleClose}
      open={isOpen}
    >
      <div className="h-full flex flex-col" id="test2">
        <div className="px-24 py-28 lg:py-32 flex gap-24 items-center border-b border-gray-200">
          <div className="heading-3 ml:display-4 flex-1">
            {window.location.href.includes('/products/')
              ? window.TMW.Strings.storeLocator.findInStore
              : window.TMW.Strings.storeLocator.findStore}
          </div>
          <button
            className="w-24 h-24 flex items-center justify-center flex-none [&>svg]:w-24 [&>svg]:h-24"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {((userSavedStoreId && !!userSavedLocation) || !!linkedLocation) && (
            <div className="p-24 ml:pb-16 bg-grey-100 lg:pb-24 lg:pt-32 ml:lg:pt-32">
              <StoreItem
                location={linkedLocation || userSavedLocation}
                hideMyStoreRow={!!linkedLocation}
              />
              <StoreProduct location={linkedLocation || userSavedLocation} />
            </div>
          )}
          <div className="px-20 lg:px-24">
            <PlacesAutocomplete ref={searchInput} />
            <button
              className="body-3-underline gap-10 flex items-center hover:no-underline"
              onClick={() => useMyLocation('manual')}
            >
              <NavigationIcon className="w-12 h-auto" />
              {window.TMW.Strings.storeLocator.useLocation}
            </button>
            {locationServicesError && (
              <p className="text-error">{locationServicesError}</p>
            )}

            {loadingResults ? (
              <div className="mt-40 animate-pulse">
                {window.TMW.Strings.storeLocator.loading}
              </div>
            ) : (
              <>
                {noResults && (
                  <div className="mt-32 md:mt-40 pb-24 md:px-24 text-center">
                    <div className="display-4 mb-16">
                      {window.TMW.Strings.search.notFoundFor.replace(
                        '{{query}}',
                        searchInput.current?.value || '',
                      )}
                    </div>
                    <div className="body-3">
                      {window.TMW.Strings.search.addressNote}
                    </div>
                  </div>
                )}
                {!noResults && (
                  <>
                    {userSavedStoreId &&
                      !!userSavedLocation &&
                      !!linkedLocation && (
                        <div className="-mb-8 mt-40">
                          <div className="flex justify-betweeen w-full">
                            <span className="eyebrow text-subdued mb-12 flex-grow">
                              {window.TMW.Strings.storeLocator.yourStore}
                            </span>
                          </div>
                          <div className="flex flex-col gap-24">
                            <div
                              key={`location-your`}
                              ref={(element) => {
                                storeRefs.current[userSavedLocation.storeId] =
                                  element;
                              }}
                              className="border border-grey-300 lc:rounded-12 overflow-hidden"
                            >
                              <StoreItem
                                className="p-24 ml:px-20 ml:tablet:px-24 pb-16 border-b border-grey-300"
                                location={userSavedLocation}
                                stockData={stockData}
                                listItem={true}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="mt-40 pb-24">
                      <div className="flex justify-betweeen w-full">
                        <span className="eyebrow text-subdued mb-12 flex-grow">
                          {userCurrentLocation &&
                            !showPopularLocations &&
                            // eslint-disable-next-line max-len
                            window.TMW.Strings.storeLocator.locationNear.replace(
                              '{{name}}',
                              userCurrentLocation.title,
                            )}
                          {(!userCurrentLocation || showPopularLocations) &&
                            window.TMW.Strings.storeLocator.popularLocations}
                        </span>
                      </div>
                      <div className="flex flex-col gap-24">
                        {showPopularLocations &&
                          popularLocations.map((location, index) => (
                            <div
                              key={`location${index}`}
                              ref={(element) => {
                                storeRefs.current[location.storeId] = element;
                              }}
                              className="border border-grey-300 lc:rounded-12 overflow-hidden"
                            >
                              <StoreItem
                                className="p-24 ml:px-20 ml:tablet:px-24 pb-16 border-b border-grey-300"
                                location={location}
                                stockData={stockData}
                                listItem={true}
                              />
                            </div>
                          ))}
                        {!showPopularLocations &&
                          results.slice(0, 10).map((location, index) => (
                            <div
                              key={`location${index}`}
                              ref={(element) => {
                                storeRefs.current[location.storeId] = element;
                              }}
                              className="border border-grey-300 lc:rounded-12 overflow-hidden"
                            >
                              <StoreItem
                                className="p-24 ml:px-20 ml:tablet:px-24 pb-16 border-b border-grey-300"
                                location={location}
                                stockData={stockData}
                                listItem={true}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default StoreLocator;
